import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChevronDown, ScrollText, Clock } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface AuditLogEntryData {
  readonly id: string
  readonly actor?: string
  readonly action: string
  readonly entityType?: string
  readonly entityId?: string
  readonly description?: string
  readonly timestamp: string
}

export interface AuditLogCardData {
  readonly message: string
  readonly entries: readonly AuditLogEntryData[]
  readonly pagination?: {
    readonly page: number
    readonly totalItems: number
    readonly pageSize: number
  }
}

const ACTION_STYLES: Record<string, string> = {
  CREATE: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
  UPDATE: 'border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/50',
  DELETE: 'border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50',
  DEPLOY: 'border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-950/50',
  UNDEPLOY: 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50',
}

const MAX_VISIBLE = 8

export function AuditLogCard({ data }: { data: AuditLogCardData }) {
  const [expanded, setExpanded] = useState(false)

  if (data.entries.length === 0) {
    return (
      <div className="my-2 rounded-lg border border-border dark:border-border/40 bg-card shadow-sm px-3.5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <ScrollText className="size-4" />
          <span>{data.message}</span>
        </div>
      </div>
    )
  }

  const needsCollapse = data.entries.length > MAX_VISIBLE
  const visible = needsCollapse && !expanded ? data.entries.slice(0, MAX_VISIBLE) : data.entries
  const hiddenCount = data.entries.length - MAX_VISIBLE

  return (
    <div className="my-2 space-y-0.5">
      <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
        <ScrollText className="size-3.5" />
        <span>{data.message}</span>
      </div>
      <div className="rounded-lg border border-border dark:border-border/40 bg-card shadow-sm overflow-hidden divide-y divide-border/50">
        {visible.map((entry) => (
          <AuditLogRow key={entry.id} entry={entry} />
        ))}
      </div>
      {needsCollapse && !expanded && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(true)}
        >
          <ChevronDown className="size-3.5" />
          Show {hiddenCount} more entries
        </Button>
      )}
    </div>
  )
}

function AuditLogRow({ entry }: { entry: AuditLogEntryData }) {
  const actionStyle = ACTION_STYLES[entry.action] ?? ACTION_STYLES.UPDATE

  return (
    <div className="flex items-center gap-3 px-3.5 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 uppercase font-semibold', actionStyle)}>
            {entry.action}
          </Badge>
          {entry.entityType && (
            <span className="text-xs text-muted-foreground font-mono">{entry.entityType}</span>
          )}
          {entry.description && (
            <span className="text-xs text-foreground truncate">{entry.description}</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
          {entry.actor && <span>{entry.actor}</span>}
          <span className="flex items-center gap-1">
            <Clock className="size-2.5" />
            {new Date(entry.timestamp).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  )
}
